import { dispatchNetworkEvent } from './event-bus';
import { isOffline } from './offline';

const scope = typeof globalThis !== 'undefined'
	? globalThis
	: typeof window !== 'undefined'
		? window
		: typeof self !== 'undefined'
			? self
			: {};

const guardKey = '__epicNetworkGuardBxAjaxPatched';
const configGuardKey = '__epicNetworkGuardBxAjaxConfigWrapped';
let guardInterval = null;

const hasNetworkCode = error => Array.isArray(error.errors)
	&& error.errors.some(item => item && typeof item.code === 'string' && item.code.toUpperCase().indexOf('NETWORK') !== -1);

const hasErrorCode = (error, code) => Array.isArray(error.errors)
	&& error.errors.some(item => item && item.code === code);

const isNetworkError = error => {
	if (!error) return false;

	if (hasNetworkCode(error)) return true;

	const rejectData = error.data && typeof error.data === 'object'
		? error.data.ajaxRejectData
		: null;

	if (!rejectData) return false;

	if (rejectData === 'timeout') return true;

	if (typeof rejectData === 'object') {
		if (typeof rejectData.status === 'number' && rejectData.status === 0) return true;

		if (typeof rejectData.type === 'string' && rejectData.type.toLowerCase().indexOf('network') !== -1) return true;
	}

	return false;
};

const handleAjaxError = (error, meta) => {
	if (hasErrorCode(error, 'invalid_csrf')) {
		dispatchNetworkEvent('epic:network:session_expired', {
			error,
			meta,
			reason: 'csrf_retry_failed',
			source: 'bx.ajax'
		});
		return;
	}

	if (!isNetworkError(error)) return;

	const detail = {
		error,
		meta,
		source: 'bx.ajax'
	};

	if (isOffline()) dispatchNetworkEvent('epic:network:offline', detail);

	else dispatchNetworkEvent('epic:network:error', detail);
};

const wrapPromise = (promise, meta) => {
	if (!promise || typeof promise.then !== 'function' || typeof promise.catch !== 'function') return promise;

	return promise.catch(error => {
		handleAjaxError(error, meta);
		throw error;
	});
};

const normalizeFailureArgs = args => {
	const [
		reason,
		data,
		requestConfig
	] = args;

	if (data && typeof data === 'object' && (data.errors || data.status)) return data;

	if (reason && typeof reason === 'object' && (reason.errors || reason.status)) return reason;

	return {
		status: 'error',
		data:   {
			ajaxRejectData: data || reason
		},
		errors: [
			{
				code:    String(reason).toUpperCase()
					.indexOf('TIMEOUT') !== -1
					? 'NETWORK_TIMEOUT'
					: 'NETWORK_ERROR',
				message: typeof reason === 'string' ? reason : 'Network error'
			}
		],
		requestConfig
	};
};

const augmentAjaxConfig = (config, meta) => {
	if (!config || typeof config !== 'object') return;

	if (config[configGuardKey]) return;

	const originalFailure = config.onfailure;

	// eslint-disable-next-line func-name-matching
	config.onfailure = function epicNetworkGuardAjaxFailureWrapper(...args) {
		const error = normalizeFailureArgs(args);

		handleAjaxError(error, meta);

		if (typeof originalFailure === 'function') return originalFailure.apply(this, args);

		return undefined;
	};

	config[configGuardKey] = true;
};

const patchAjaxMethods = () => {
	const bx = scope && scope.BX ? scope.BX : null;
	let ajax = bx && bx.ajax ? bx.ajax : null;

	if (!ajax) return false;

	if (ajax[guardKey]) return true;

	// eslint-disable-next-line no-underscore-dangle
	if (!ajax.__epicNetworkGuardBaseWrapped) {
		const originalAjaxFn = ajax;

		// eslint-disable-next-line func-name-matching,func-style
		const wrappedAjaxFn = function epicNetworkGuardAjax(config, ...rest) {
			if (config && typeof config === 'object') augmentAjaxConfig(config, { method: 'ajax', args: [ config, ...rest ] });

			// eslint-disable-next-line no-invalid-this
			return originalAjaxFn.apply(this, [ config, ...rest ]);
		};

		Object.keys(originalAjaxFn).forEach(key => {
			wrappedAjaxFn[key] = originalAjaxFn[key];
		});

		// eslint-disable-next-line no-underscore-dangle
		wrappedAjaxFn.__epicNetworkGuardBaseWrapped = true;
		// eslint-disable-next-line no-underscore-dangle
		wrappedAjaxFn.__epicNetworkGuardOriginal = originalAjaxFn;

		bx.ajax = wrappedAjaxFn;
	}

	ajax = bx.ajax;

	if (ajax[guardKey]) return true;

	const ajaxPromise = ajax.promise;

	if (typeof ajaxPromise === 'function') {
		// eslint-disable-next-line func-name-matching
		ajax.promise = function epicNetworkGuardAjaxPromiseWrapper(...args) {
			const promise = ajaxPromise.apply(this, args);

			return wrapPromise(promise, { method: 'ajax.promise', args });
		};
	}

	const methodsToWrap = [ 'runAction', 'runComponentAction' ];

	methodsToWrap.forEach(method => {
		const original = ajax[method];

		if (typeof original !== 'function') return;

		ajax[method] = function epicNetworkGuardAjaxWrapper(...args) {
			const configCandidate = args[2] || args[1];

			if (configCandidate && typeof configCandidate === 'object') augmentAjaxConfig(configCandidate, { method, args });

			const promise = original.apply(this, args);

			return wrapPromise(promise, { method, args });
		};
	});

	ajax[guardKey] = true;
	return true;
};

const ensureBxAjaxGuard = () => {
	if (patchAjaxMethods()) {
		if (guardInterval) {
			clearInterval(guardInterval);
			guardInterval = null;
		}

		return;
	}

	if (guardInterval) return;

	guardInterval = setInterval(() => {
		if (patchAjaxMethods()) {
			clearInterval(guardInterval);
			guardInterval = null;
		}
	}, 500);
};

export { ensureBxAjaxGuard };
